// Converted from https://mseng.visualstudio.com/VSOnline/_git/PerfTests?path=%2FTools%2FTraceParser%2FTraceParser%2FTraceParser.cs&version=GBmaster

/// <amd-module name="TFS/DRITools/util/TraceParser" />

import { Obj } from "TFS/DRITools/util/RequestNode";

/**
 * Class to represent an intermediate trace, consists of a header and a body
 */
interface IntermediateTrace {
    header?: string | null;
    body?: string;
}

/**
 * Trace method. Can be a root, sproc or a regular method. Consists of name, start, end, and data
 */
export class TraceMethod {
    public name: string;
    public data: any;
    public isRoot: boolean;
    public isSproc: boolean;
    public start: number;
    public end: number;
    public children: TraceMethod[];

    constructor() {
        this.children = [];
        this.isRoot = false;
        this.isSproc = false;
        this.start = 0;
        this.end = 0;
    }
}

/**
 * Result of the trace parser, consists of roots, specifies if the out of order method was detected
 */
export class TraceParserResult {
    public outOfOrderMethodsDetected: boolean;
    public roots: TraceMethod[];

    constructor(outOfOrderMethodsDetected: boolean, roots: TraceMethod[]) {
        this.outOfOrderMethodsDetected = outOfOrderMethodsDetected;
        this.roots = roots;
    }
}

const s_headerDataRegExString = "(\\((\\w+)\\s(\\d+(\\.\\d+)?)(s|ms)?\\))";
const s_headerRegExString = "(Total Time:(\\d+(\\.\\d+)?).*?)";
const s_fullHeaderRegExString = "(Total Time:(\\d+(\\.\\d+)?)(\\s\\((\\w+)\\s(\\d+(\\.\\d+)?)(s|ms)?\\))+)";

const s_globalRegExString: string =
    "(((\\w+\\.)*SqlResourceComponent\\.Execute))\\s(.*?):(\\d+)\\s?|" +
    "(([\\w\\.\\(\\)]+)(\\s\\S+)?:(\\d+)\\s?)|" +
    "(([\\w\\.\\(\\)]+)(\\s\\S+)?:-(\\d+)\\s?)|" +
    "((\\n|\\r|\\r\\n))|" +
    "(\\s)";

const s_globalRegEx = new RegExp(s_globalRegExString, "g");
const s_headerRegEx = new RegExp(s_headerRegExString, "g");
const s_fullHeaderRegEx = new RegExp(s_fullHeaderRegExString, "g");
const s_headerDataRegEx = new RegExp(s_headerDataRegExString, "g");
const s_methodNameRegEx = new RegExp("([\\w\\.\\(\\)]+):-?(\\d+)", "mg");

/**
 * Parses the raw string input to be in a tree form for future parsing
 * @param rawTraces
 */
export function parseTrace(rawTraces: string): TraceParserResult {
    s_globalRegEx.lastIndex = 0;
    s_headerRegEx.lastIndex = 0;
    s_fullHeaderRegEx.lastIndex = 0;
    s_headerDataRegEx.lastIndex = 0;
    s_methodNameRegEx.lastIndex = 0;

    const roots: TraceMethod[] = [];
    const wellFormedTrace: TraceMethod[] = [];

    let outOfOrderMethodsDetected = false;

    const traces: IntermediateTrace[] = cleanupAndSplitTraces(rawTraces);

    traces.forEach(trace => {
        const root = new TraceMethod();
        root.isRoot = true;

        if (!trace.header || trace.header.length == 0) {
            root.name = "Trace headers missing";
        } else {
            const headerData: Obj = {};
            const headerString: string = trace.header;

            let headerDataMatch: any;
            while ((headerDataMatch = s_headerDataRegEx.exec(headerString)) != null) {
                headerData[headerDataMatch[2]] = headerDataMatch[3];
            }
            const headerMatches: any = s_headerRegEx.exec(headerString);
            headerData["Total Time"] = headerMatches[2];

            root.name = headerString;
            root.data = headerData;
        }
        roots.push(root);

        let match: any;
        while (trace.body && (match = s_globalRegEx.exec(trace.body))) {
            if (match[6]) {
                const openingMethodName: string = match[7];
                const openingTimeStamp: number = +match[9];

                const method = new TraceMethod();
                method.name = openingMethodName;
                method.start = openingTimeStamp;
                method.end = -1;

                if (wellFormedTrace.length === 0) {
                    roots[roots.length - 1].children.push(method);
                } else {
                    wellFormedTrace[wellFormedTrace.length - 1].children.push(method);
                }
                wellFormedTrace.push(method);
            } else if (match[10]) {
                let lastStoredSample: TraceMethod;

                const closingMethodName: string = match[11];
                const closingTimeStamp: number = +match[13];

                // Starting with one since the first entry in the stack is the root
                if (wellFormedTrace && wellFormedTrace.length > 0) {
                    lastStoredSample = wellFormedTrace[wellFormedTrace.length - 1];
                    if (lastStoredSample.name != closingMethodName) {
                        if (lastStoredSample.isSproc) {
                            for (let i = 0; i < 2; i++) {
                                const poped: TraceMethod | undefined = wellFormedTrace.pop();
                                if (!poped) {
                                    return;
                                }
                                poped.end = closingTimeStamp;
                            }
                        } else {
                            outOfOrderMethodsDetected = true;
                            const previousTraceMethod: TraceMethod | undefined = wellFormedTrace.pop();

                            if (!previousTraceMethod) {
                                return;
                            }
                            // Check if this is closing the N-1 sample
                            if (wellFormedTrace.length > 0) {
                                // Note that stack elements are counted from the top-down
                                const secondToLastStoredSample = wellFormedTrace[wellFormedTrace.length - 1];
                                if (secondToLastStoredSample.name === closingMethodName) {
                                    const poped: TraceMethod | undefined = wellFormedTrace.pop();
                                    if (!poped) {
                                        return;
                                    }
                                    poped.end = closingTimeStamp;
                                } else {
                                    // Reprocess the previous sample and mark as broken and keep going
                                    const method = new TraceMethod();
                                    method.name = closingMethodName;
                                    method.start = closingTimeStamp;
                                    method.end = -1;
                                    lastStoredSample.children.push(method);
                                }
                            } else {
                                wellFormedTrace.push(previousTraceMethod);

                                // Push the unbalanced method
                                const method = new TraceMethod();
                                method.name = closingMethodName;
                                method.start = closingTimeStamp;
                                method.end = -1;
                                previousTraceMethod.children.push(method);
                            }
                        }
                    } else {
                        const closingTrace: TraceMethod | undefined = wellFormedTrace.pop();
                        if (!closingTrace) {
                            return;
                        }
                        closingTrace.end = closingTimeStamp;
                        closingTrace.children
                            .filter(c => c.isSproc)
                            .forEach(child => {
                                child.end = closingTimeStamp;
                            });
                    }
                } else {
                    outOfOrderMethodsDetected = true;
                    const tm = new TraceMethod();
                    tm.name = closingMethodName;
                    tm.start = closingTimeStamp;
                    tm.end = -1;

                    if (wellFormedTrace.length == 0) {
                        roots[roots.length - 1].children.push(tm);
                    } else {
                        wellFormedTrace[wellFormedTrace.length - 1].children.push(tm);
                    }
                }
            } else if (match[1]) {
                // The SqlComponent.Execute block opening needs special handling since since it includes:
                //  Sproc name
                //  Data source name
                //  Database name
                const openingTimeStamp: number = +match[5];

                const method = new TraceMethod();
                method.name = match[2];
                method.start = openingTimeStamp;

                if (wellFormedTrace.length == 0) {
                    roots[roots.length - 1].children.push(method);
                } else {
                    wellFormedTrace[wellFormedTrace.length - 1].children.push(method);
                }
                wellFormedTrace.push(method);

                // The SqlComponent.Execute block might include a code snippet which could be multiline so deal with that here
                const lines: string[] = match[4].trim().split(/\n|\r\n/);

                // Special treatment for the line containing the sproc name to add the link to search
                const firstLine = lines.filter(l => l && l.length > 0)[0].split(" ");
                const sprocName = firstLine.filter(l => l && l.length > 0)[0];

                const sproc = new TraceMethod();
                sproc.name = sprocName;
                sproc.start = openingTimeStamp;
                sproc.isSproc = true;
                method.children.push(sproc);

                sproc.data = [firstLine.splice(1).join(" ")].concat(lines.splice(1));
            }
        }
        // Close all open methods
        while (wellFormedTrace && wellFormedTrace.length > 0) {
            const closedMethod = wellFormedTrace.pop();
            if (!closedMethod) {
                return;
            }
            closedMethod.end = -1;
        }
    });
    return new TraceParserResult(outOfOrderMethodsDetected, roots);
}

/**
 * Cleans up and splits traces if multiple separate traces were provided
 * @param rawTrace
 */
function cleanupAndSplitTraces(rawTrace: string): IntermediateTrace[] {
    const intermediateTraces: IntermediateTrace[] = [];
    const intermediateTracesSecondPass: IntermediateTrace[] = [];
    rawTrace = rawTrace.trim();
    let lines: string[] = rawTrace.split("\n");

    let traceStart = 0;
    let interTrace: IntermediateTrace = {};

    for (let i = 0; i < lines.length; i++) {
        const headerMatch = s_fullHeaderRegEx.exec(lines[i]);

        if (headerMatch) {
            if (i > 0) {
                interTrace.body = lines
                    .slice(traceStart)
                    .slice(0, i - traceStart)
                    .join("\n")
                    .trim();
                intermediateTraces.push(interTrace);
                traceStart = i;
            }

            interTrace.header = headerMatch[0];

            if (headerMatch.length < lines[i].trim().length) {
                lines[i] = lines[i].substring(headerMatch[0].length).trim();
            } else {
                lines[i] = "";
            }
        }
    }

    interTrace.body = lines
        .slice(traceStart)
        .join("\n")
        .trim();
    intermediateTraces.push(interTrace);

    intermediateTraces.forEach(intermediateTrace => {
        lines = intermediateTrace.body ? intermediateTrace.body.split(" ") : [];
        let previousTimestamp = 0;
        for (let i = 0; i < lines.length; i++) {
            let methodNameMatch: any = s_methodNameRegEx.exec(lines[i]);
            if (methodNameMatch === null) {
                const ind: number = lines[i].indexOf("\n\n");
                if (ind >= 0) {
                    lines[i] = lines[i].substring(ind);
                    for (let j: number = i - 1; j > 0; j--) {
                        if (!s_methodNameRegEx.exec(lines[i])) {
                            lines[j] = "";
                        } else {
                            break;
                        }
                    }
                }
                continue;
            } else {
                while (methodNameMatch !== null) {
                    const timestamp: number = +methodNameMatch[2];
                    if (timestamp < previousTimestamp) {
                        lines[i] = lines[i].replace(new RegExp(/\n$/g), "\0")
                            .replace(new RegExp(/\n/g), " "); //keeping the last line feed as it is, and making all other next line feed as white space.
                        break;
                    }
                    previousTimestamp = timestamp;
                    methodNameMatch = s_methodNameRegEx.exec(lines[i]);
                }
            }
        }

        const newTraces = lines
            .join(" ")
            .split("\0")
            .filter(l => {
                return l != null && l.length != 0;
            });

        interTrace = {};
        interTrace.header = intermediateTrace.header;
        interTrace.body = newTraces[0];
        intermediateTracesSecondPass.push(interTrace);

        newTraces.splice(1).forEach(trace => {
            interTrace = {};
            interTrace.header = null;
            interTrace.body = trace;
            intermediateTracesSecondPass.push(interTrace);
        });
    });
    return intermediateTracesSecondPass;
}
